/**
 * Tier comparison — pure helpers.
 *
 * Mirrors the server-side tier rank in
 * `supabase/functions/_shared/credits/server-enforce.ts` (see
 * `userTierCoversModel`). The lowercase strings here are the client-side
 * tier shape used by `ImageModel.tier` and the `userTier` prop carried
 * around the React tree.
 */
const TIER_RANK = {
    anonymous: 0,
    free: 1,
    pro: 2,
    max: 3,
    enterprise: 4,
};
/**
 * True iff the user's subscription tier covers the model's required tier.
 * Returned values agree with the server's tier-gate decision so the UI
 * can avoid showing "spend N ju to unlock" for a model the user already
 * has access to.
 */
export function canAccessTier(modelTier, userTier) {
    const modelRank = TIER_RANK[modelTier.toLowerCase()] ?? 0;
    const userRank = TIER_RANK[userTier.toLowerCase()] ?? 1;
    return userRank >= modelRank;
}
/** Convenience for typed model objects. */
export function canAccessModel(model, userTier) {
    return canAccessTier(model.tier, userTier);
}
/**
 * Map of subscription plan id → effective tier slug. Plus + Pro both
 * grant `pro` tier (Plus is intermediate price-point, same gating
 * rights). Max grants `max`. Unknown plan ids are ignored — caller
 * falls back to profile tier.
 */
const SUBSCRIPTION_PLAN_TO_TIER = {
    plus: 'pro',
    pro: 'pro',
    max: 'max',
};
/** True iff the subscription is currently entitled (active or trialing). */
function isSubscriptionEntitled(sub) {
    return sub.status === 'active' || sub.status === 'trialing';
}
/**
 * Pure-fn — derive the effective tier from a profile tier + (optional)
 * subscription row. The HIGHER of the two wins:
 *   - Profile tier `'max'` (admin-set) always beats subscription
 *     `'pro'` — admins keep their override.
 *   - An active `'max'` subscription beats profile tier `'free'` or
 *     `'pro'` — the user paid, give them the access.
 *   - A canceled / past_due subscription is ignored — fall back to
 *     profile tier (which is `'free'` for the typical post-cancel case).
 *
 * This helper is the source-of-truth for "what tier is this user RIGHT
 * NOW?" once subscriptions ship. UI surfaces and server gates should
 * read through here rather than `profile.tier` directly.
 *
 * Returns the same string shape used by `canAccessTier` so it composes
 * naturally: `canAccessTier(model.tier, resolveEffectiveTier(profile, sub))`.
 */
export function resolveEffectiveTier(profileTier, subscription) {
    const profileRank = TIER_RANK[profileTier.toLowerCase()] ?? 1;
    if (!subscription || !isSubscriptionEntitled(subscription)) {
        return profileTier;
    }
    const subscriptionTier = SUBSCRIPTION_PLAN_TO_TIER[subscription.planId.toLowerCase()];
    if (!subscriptionTier) {
        // Unknown plan id (e.g. legacy 'enterprise' rank we haven't wired
        // yet) — fall through to profile tier rather than guessing.
        return profileTier;
    }
    const subRank = TIER_RANK[subscriptionTier];
    return subRank > profileRank ? subscriptionTier : profileTier;
}
